import {
  PROTOCOL_VERSION,
  assertNever,
  inviteJoinRequestSchema,
  inviteJoinResponseSchema,
  type ColyseusSeatReservation,
  type InviteJoinErrorCode,
} from '@2d-space/contracts';

export type InviteErrorTone = 'invalid' | 'unavailable' | 'temporary';

export interface InviteErrorViewState {
  readonly code: InviteJoinErrorCode;
  readonly title: string;
  readonly description: string;
  readonly tone: InviteErrorTone;
  readonly canRetry: boolean;
  readonly retryAfterSeconds: number | undefined;
}

export type InviteReservationOutcome =
  | {
      readonly ok: true;
      readonly reservation: ColyseusSeatReservation;
      readonly browserCredential?: string;
    }
  | { readonly ok: false; readonly error: InviteErrorViewState };

interface InviteRequestInput {
  readonly serverUrl: string;
  readonly slug: string | undefined;
  readonly token: string | undefined;
  readonly browserCredential?: string;
  readonly roomCapability?: string;
}

interface InviteHttpResponse {
  readonly headers: Pick<Headers, 'get'>;
  json(): Promise<unknown>;
}

export type InviteHttpRequest = (
  input: string,
  init: {
    readonly method: 'POST';
    readonly headers: Readonly<Record<string, string>>;
    readonly body: string;
  },
) => Promise<InviteHttpResponse>;

export async function requestInviteReservation(
  input: InviteRequestInput,
  request: InviteHttpRequest = globalThis.fetch,
): Promise<InviteReservationOutcome> {
  const joinRequest = inviteJoinRequestSchema.safeParse({
    version: PROTOCOL_VERSION,
    slug: input.slug,
    token: input.token,
    browserCredential: input.browserCredential,
    roomCapability: input.roomCapability,
  });
  if (!joinRequest.success) {
    // Missing tokens and malformed route inputs are knowable locally. Avoid
    // spending rate-limit budget on a request the server can only reject.
    return { ok: false, error: inviteErrorViewState('invalid-request') };
  }

  const response = await request(new URL('/matchmake/invite', input.serverUrl).toString(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(joinRequest.data),
  });

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { ok: false, error: inviteErrorViewState('internal-error') };
  }

  const parsed = inviteJoinResponseSchema.safeParse(body);
  if (!parsed.success) {
    return { ok: false, error: inviteErrorViewState('internal-error') };
  }
  if (parsed.data.ok) {
    return {
      ok: true,
      reservation: parsed.data.reservation,
      ...(parsed.data.browserCredential === undefined
        ? {}
        : { browserCredential: parsed.data.browserCredential }),
    };
  }

  const retryAfterSeconds =
    parsed.data.error.code === 'rate-limited'
      ? (readRetryAfterSeconds(response.headers.get('Retry-After')) ??
        parsed.data.error.retryAfterSeconds)
      : parsed.data.error.retryAfterSeconds;
  return {
    ok: false,
    error: inviteErrorViewState(parsed.data.error.code, retryAfterSeconds),
  };
}

export function inviteErrorViewState(
  code: InviteJoinErrorCode,
  retryAfterSeconds?: number,
): InviteErrorViewState {
  switch (code) {
    case 'invalid-request':
      return {
        code,
        title: 'This invite link is incomplete',
        description:
          'Ask the person who invited you to send the complete link, including its token.',
        tone: 'invalid',
        canRetry: false,
        retryAfterSeconds: undefined,
      };
    case 'invalid-signature':
      return {
        code,
        title: 'This invite link is not valid',
        description:
          'The link may have been altered. Ask the person who invited you for a new one.',
        tone: 'invalid',
        canRetry: false,
        retryAfterSeconds: undefined,
      };
    case 'invalid-claims':
      return {
        code,
        title: 'This invite cannot be used',
        description:
          'Its room details are not valid. Ask the person who invited you for a new link.',
        tone: 'invalid',
        canRetry: false,
        retryAfterSeconds: undefined,
      };
    case 'expired':
      return {
        code,
        title: 'This invite has expired',
        description: 'Ask the person who invited you to create a fresh link.',
        tone: 'invalid',
        canRetry: false,
        retryAfterSeconds: undefined,
      };
    case 'revoked':
      return {
        code,
        title: 'This invite is no longer active',
        description:
          'Access for this link was withdrawn. Contact the room host if you still need to join.',
        tone: 'invalid',
        canRetry: false,
        retryAfterSeconds: undefined,
      };
    case 'unknown-slug':
      return {
        code,
        title: 'This link points to a different room',
        description:
          'Open the original invite without changing the room name, or ask for a new link.',
        tone: 'invalid',
        canRetry: false,
        retryAfterSeconds: undefined,
      };
    case 'room-full':
      return {
        code,
        title: 'This room is full right now',
        description: 'Your invite is fine. Try again after someone leaves.',
        tone: 'unavailable',
        canRetry: true,
        retryAfterSeconds: undefined,
      };
    case 'room-locked':
      return {
        code,
        title: 'This room is temporarily locked',
        description:
          'Your invite is fine. The host can unlock the room when it is ready for more people.',
        tone: 'unavailable',
        canRetry: true,
        retryAfterSeconds: undefined,
      };
    case 'approval-required':
      return {
        code,
        title: 'Approval is required',
        description: 'A room owner or admin must approve this browser before you can enter.',
        tone: 'unavailable',
        canRetry: true,
        retryAfterSeconds: undefined,
      };
    case 'browser-blocked':
      return {
        code,
        title: 'This browser is blocked',
        description: 'This browser cannot re-enter this room. Contact the room owner.',
        tone: 'unavailable',
        canRetry: false,
        retryAfterSeconds: undefined,
      };
    case 'rate-limited':
      return {
        code,
        title: 'Too many join attempts',
        description: 'Please wait before trying this invite again.',
        tone: 'temporary',
        canRetry: true,
        retryAfterSeconds,
      };
    case 'internal-error':
      return {
        code,
        title: 'The room could not be reached',
        description:
          'The invite may still be valid. Try again, or contact the host if the problem continues.',
        tone: 'temporary',
        canRetry: true,
        retryAfterSeconds: undefined,
      };
    default:
      return assertNever(code);
  }
}

export function buildInviteShareUrl(origin: string, slug: string, token: string): string {
  const url = new URL(`/r/${encodeURIComponent(slug)}`, origin);
  url.searchParams.set('t', token);
  return url.toString();
}

function readRetryAfterSeconds(value: string | null): number | undefined {
  if (value === null || !/^\d+$/.test(value)) {
    return undefined;
  }
  const seconds = Number(value);
  return Number.isSafeInteger(seconds) && seconds > 0 ? seconds : undefined;
}
